import React, { useState } from 'react'
import { useTheme, alpha } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { request } from '../../_network/request'
import { clientAccountsURLs } from '../../_network/apiUrls'
import { isValidEmail, isValidPassword } from '../_common/_helpers/validation'
import CustomTextField from '../_common/_form_fields/CustomTextField'
import CustomSelectField from '../_common/_form_fields/CustomSelectField'
import CustomBackdrop from '../_common/CustomBackdrop'

const UserAdd = (props) => {
  const { handleAddUserDialogClose, setFetchUsers } = props
  const theme = useTheme()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')  
  const [userRole, setUserRole] = useState('')
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const roleList = [
    {
      keyword: 'Admin',
      value: 'admin',
    },
    {
      keyword: 'User',
      value: 'user',
    },
  ]

  const formControlStyle = {
    background: theme.palette.aliceBlue,
    color: theme.palette.vodka,
    borderRadius: '5px',
    width: '100%',
  }

  const outlinedInputStyle = {
    '& .MuiInputBase-input::label': {
      color: theme.palette.vodka,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.vodka,
    },
  }

  const errorHelperStyle = {
    color: theme.palette.coralRed,
    paddingLeft: '5px',
  }

  const helperStyle = {
    color: theme.palette.grayX11,
    paddingLeft: '5px',
  }

  const fieldBoxStyle = {
    padding: '12px 0px'
  }

  const saveButtonStyle = {
    fontWeight: '500',
    color: 'white',
    background: theme.palette.greenSheen,
    fontSize: '18px',
    height: '40px',
    borderRadius: '20px',
    padding: '0px 30px',
    marginTop: '20px',
    textTransform: 'capitalize',

    '&:hover': {
      background: alpha(theme.palette.greenSheen, 0.8),
    },
  }

  const handleChangeEmail = (e) => {
    const value = e.target.value
    setEmail(value)
    if (isValidEmail(value)) {
      setErrors(prev => {return { ...prev, email: null }})
    }
  }

  const handleChangePassword = (e) => {
    const value = e.target.value
    setPassword(value)
    if (isValidPassword(value)) {
      setErrors(prev => {return { ...prev, password: null }})
    }    
  }

  const handleChangeConfirmPassword = (e) => {
    const value = e.target.value
    setConfirmPassword(value)
    if (value === password) {
      setErrors(prev => {return { ...prev, confirmPassword: null }})
    }    
  }

  const handleChangeFirstName = (e) => {
    const value = e.target.value
    setFirstName(value)
    if (value) {
      setErrors(prev => {return { ...prev, firstName: null }})
    }    
  }

  const handleChangeLastName = (e) => {
    const value = e.target.value
    setLastName(value)
    if (value) {
      setErrors(prev => {return { ...prev, lastName: null }})
    }   
  }

  const handleChangeUserRole = (value) => {
    setUserRole(value)
    if (value) {
      setErrors(prev => {return { ...prev, userRole: null }})
    }    
  }

  const scrollToTop = () => {
    const element = document.getElementById('formStart')
    element.scrollIntoView({ behavior: 'smooth' })
  }

  const addUserRequest = async (postData) => {
    const url = constants.core_service_domain + '/' + clientAccountsURLs.getUsers
      
    const request_data = { url, method: 'POST', data: postData }
    
    const response = await request(request_data)
    
    if (response && response.status_code && response.status_code < 400) {
      handleAddUserDialogClose()
      setFetchUsers(prev => prev + 1)
    } else {
      scrollToTop()
      setErrors(prev => {
        return {
          ...prev,
          generalError: defaultTexts.anErrorOccured,
        }
      })
    }
  }

  const handleClickSubmit = async () => {
    setIsLoading(true)
    let _errors = {}

    if (!firstName) {
      _errors.firstName = defaultTexts.pleaseFillOut
    }

    if (!lastName) {
      _errors.lastName = defaultTexts.pleaseFillOut
    }

    if (!email) {
      _errors.email = defaultTexts.pleaseFillOut
    } else if (!isValidEmail(email)) {
      _errors.email = defaultTexts.invalidEmail
    }

    if (password && !isValidPassword(password)) {
      _errors.password = defaultTexts.passwordRequirements
    }

    if (password && !confirmPassword) {
      _errors.confirmPassword = defaultTexts.pleaseFillOut
    } else if (password && confirmPassword !== password) {
      _errors.confirmPassword = defaultTexts.passwordDoesNotMatch
    }

    if (!userRole) {
      _errors.userRole = defaultTexts.pleaseFillOut
    }

    if (Object.keys(_errors).length === 0) {
      let postData = {
        firstName,
        lastName,
        email,
        role: userRole,
      }

      if (password) {
        postData.password = password
        postData.confirm_password = confirmPassword
      }

      addUserRequest(postData)
    }
    
    if (Object.keys(_errors).length > 0) {
      scrollToTop()
      _errors.generalError = defaultTexts.anErrorOccured
      setErrors(_errors)
    }

    setIsLoading(false)
  }

  return (
    <Box id='formStart'>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      {
        errors.generalError &&
        <Alert severity='error' sx={{ fontSize: '16px' }}>{errors.generalError}</Alert>
      }

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='outlined'
          label={defaultTexts.firstName}
          currentValue={firstName}
          setCurrentValue={handleChangeFirstName}
          required={true}
          error={errors.firstName ? true : false}
          loginHelperText={errors.firstName}
          loginHelperSx={errorHelperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='outlined'
          label={defaultTexts.lastName}
          currentValue={lastName}
          setCurrentValue={handleChangeLastName}
          required={true}
          error={errors.lastName ? true : false}
          loginHelperText={errors.lastName}
          loginHelperSx={errorHelperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField 
          variant='outlined'
          label={defaultTexts.email}
          currentValue={email}
          setCurrentValue={handleChangeEmail}
          required={true}
          error={errors.email ? true : false}
          loginHelperText={errors.email}
          loginHelperSx={errorHelperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='outlined'
          label={defaultTexts.password + ' (' + defaultTexts.optional + ')'}
          currentValue={password}
          setCurrentValue={handleChangePassword}
          error={errors.password ? true : false}
          loginHelperText={defaultTexts.passwordRequirements}
          loginHelperSx={errors.password ? errorHelperStyle : helperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomTextField
          type='password'
          variant='outlined'
          label={defaultTexts.confirmPassword + ' (' + defaultTexts.optional + ')'}
          currentValue={confirmPassword}
          setCurrentValue={handleChangeConfirmPassword}
          error={errors.confirmPassword ? true : false}
          loginHelperText={errors.confirmPassword}
          loginHelperSx={errorHelperStyle}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Box sx={fieldBoxStyle}>
        <CustomSelectField
          label={defaultTexts.userRole}
          items={roleList} 
          currentValue={userRole}
          setCurrentValue={handleChangeUserRole}
          required={true}
          error={errors.userRole ? true : false}
          loginHelperText={errors.userRole}
          loginHelperSx={errorHelperStyle}
          self_item_titles={true}
          size='small'
          sx={formControlStyle}
          outlinedInputSx={outlinedInputStyle}
        />
      </Box>

      <Stack
        direction='row'
        justifyContent='flex-end'
      >
        <Button
          variant='contained'
          disableRipple
          disableElevation
          disabled={false}
          onClick={handleClickSubmit}
          sx={saveButtonStyle}
        >
          {defaultTexts.save}
        </Button>
      </Stack>
    </Box>
  )
}

export default UserAdd